<template>
<PageSection>
    <PageHeader id="page-header" title="Store Configuration">
        <template v-if="storeData" #alert>
            <button
                @click="startEditMode"
                class="btn btn-primary px-4 py-2 text-sm font-medium rounded-md"
                :disabled="!!storeDetailError"
            >
                Edit
            </button>
        </template>
    </PageHeader>

    <!-- Loading State -->
    <div v-if="isLoadingStore && !storeData && !storeDetailError" class="flex items-center justify-center py-20">
        <div class="text-center">
            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
            <p class="text-gray-600">Loading store information...</p>
        </div>
    </div>

    <!-- No Store State -->
    <div v-else-if="!storeData && (!storeDetailError || storeDetailError.message?.includes('not found'))" class="bg-white border rounded-lg shadow-sm">
        <CreateStore
            @cancel="() => {}"
            @success="handleCreateSuccess"
        />
    </div>

    <!-- Error State -->
    <div v-else-if="!storeData && storeDetailError && !storeDetailError.message?.includes('not found')" class="flex items-center justify-center py-20">
        <div class="text-center max-w-md">
            <h2 class="text-xl font-semibold text-red-600 mb-4">Error Loading Store</h2>
            <p class="text-gray-600 mb-6">{{ storeDetailError.message || 'Failed to load store details. Please try again.' }}</p>
            <button @click="retryLoadStore" class="btn btn-secondary px-6 py-3">
                Retry
            </button>
        </div>
    </div>

    <!-- Edit Mode -->
    <div v-if="storeData && isEditMode" class="bg-white border rounded-lg shadow-sm">
        <CreateStore
            mode="update"
            :initial-data="initialStoreData"
            @cancel="cancelEditMode"
            @success="handleUpdateSuccess"
        />
    </div>

    <!-- View Mode -->
    <div v-else-if="storeData && !isEditMode" class="grid grid-cols-12 gap-4 sm:grid-cols-12">
        <!-- Left Column - System Configuration -->
        <div class="col-span-6 bg-white border rounded-md shadow">
            <div class="p-4 border-b">
                <h3 class="text-lg font-semibold text-gray-900 mb-1">System Configuration</h3>
                <p class="text-sm text-gray-600">Details about the store</p>
            </div>
            <div class="p-4">
                <Datalist :list="systemConfiguration" />
            </div>
        </div>

        <!-- Right Column -->
        <div class="col-span-6 flex flex-col gap-4">
            <!-- Address Information -->
            <div class="bg-white border rounded-md shadow">
                <div class="p-4 border-b">
                    <h3 class="text-lg font-semibold text-gray-900 mb-1">Address Information</h3>
                    <p class="text-sm text-gray-600">Physical location details of the store</p>
                </div>
                <div class="p-4">
                    <Datalist :list="physicalInformation" />
                </div>
            </div>

            <!-- Contact Information -->
            <div class="bg-white border rounded-md shadow">
                <div class="p-4 border-b">
                    <h3 class="text-lg font-semibold text-gray-900 mb-1">Contact Information</h3>
                    <p class="text-sm text-gray-600">Contacts for the store</p>
                </div>
                <div class="p-4">
                    <Datalist :list="contactInformation" />
                </div>
            </div>
        </div>
    </div>
</PageSection>
</template>

<script lang="ts" setup>
import { useQuery, useQueryCache } from "@pinia/colada";
import { computed, ref } from "vue";
import { definePage } from "vue-router/auto";
import CreateStore from "../../components/Store/CreateStore.vue";
import Datalist from "../../components/Store/Datalist.vue";
import { useToast } from "../../composables/useToast";
import { trpc } from "../../trpc/client";
import { mapStoreToFormData } from "../../utils/store-model";

const isEditMode = ref(false);

definePage({
	meta: {
		requiresAuth: true,
		featureFlag: "store-management",
	},
});

const {
	error: storeDetailError,
	data: storeData,
	isLoading: isLoadingStore,
	refetch: refetchStoreData,
} = useQuery({
	key: ["store"],
	query: () => trpc.storeManagement.getStoreDetails.query({}),
	gcTime: 1000 * 60 * 10,
	staleTime: 1000 * 60 * 5,
});

const { data: storeContactData } = useQuery({
	key: ["storeStaff"],
	query: () => trpc.storeManagement.getStoreStaff.query({}),
	gcTime: 1000 * 60 * 10,
	staleTime: 1000 * 60 * 5,
});

const { showErrorToast, showSuccessToast } = useToast();
const queryCache = useQueryCache();

const systemConfiguration = computed(() => {
	if (!storeData?.value) return [];
	return [
		{ label: "Store Number", value: storeData.value.store_number },
		{ label: "Provider", value: storeData.value.provider },
		{ label: "Email", value: storeData.value.email },
		{
			label: "Enable Course Import Email",
			value: storeData.value.send_course_import_email ? "Yes" : "No",
		},
		{ label: "Website", value: storeData.value.website },
	];
});

const physicalInformation = computed(() => {
	if (!storeData?.value) return [];
	return [
		{ label: "Address", value: storeData.value.street_address },
		{
			label: "Phone",
			value: storeData.value.phone?.join(", ") || "",
		},
		{
			label: "Fax",
			value: storeData.value.fax?.join(", ") || "",
		},
		{ label: "Country", value: storeData.value.country },
		{ label: "City", value: storeData.value.city },
		{ label: "State", value: storeData.value.state },
		{ label: "Zipcode", value: storeData.value.zipcode },
	];
});

const contactInformation = computed(() => {
	const contacts = storeContactData.value || [];
	return contacts.map((contact) => ({
		label: contact.role,
		value: `${contact.name} (${contact.email})`,
	}));
});

const handleCreateSuccess = (storeNumber: number) => {
	showSuccessToast(`Store ${storeNumber} created successfully!`);
};

const handleUpdateSuccess = (storeNumber: number) => {
	showSuccessToast(`Store ${storeNumber} updated successfully!`);
	isEditMode.value = false;
};

const initialStoreData = computed(() =>
	storeData.value ? mapStoreToFormData(storeData.value) : undefined,
);

const startEditMode = () => {
	isEditMode.value = true;
};

const cancelEditMode = () => {
	isEditMode.value = false;
};

const retryLoadStore = async () => {
	try {
		queryCache.invalidateQueries({
			predicate: (query) =>
				Array.isArray(query.key) &&
				(query.key[0] === "store" || query.key[0] === "storeStaff"),
		});
		await refetchStoreData();
		showSuccessToast("Store data refreshed successfully");
	} catch (error) {
		showErrorToast("Failed to reload store data. Please try again.");
	}
};
</script>
